<script setup lang="ts">
/**
 * Icon tile for a file: the glyph of its kind (image, PDF, spreadsheet, …) on
 * the kind's tinted background. Kind from `kind`, else from the MIME `type`,
 * else from the `name`'s extension. Decorative — the file name next to it
 * carries the meaning.
 */
import File from 'lucide-vue-next/dist/esm/icons/file.js'; import FileArchive from 'lucide-vue-next/dist/esm/icons/file-archive.js'; import FileAudio from 'lucide-vue-next/dist/esm/icons/file-headphone.js'; import FileCode2 from 'lucide-vue-next/dist/esm/icons/file-code-corner.js'; import FileImage from 'lucide-vue-next/dist/esm/icons/file-image.js'; import FileSpreadsheet from 'lucide-vue-next/dist/esm/icons/file-spreadsheet.js'; import FileText from 'lucide-vue-next/dist/esm/icons/file-text.js'; import FileType2 from 'lucide-vue-next/dist/esm/icons/file-type-corner.js'; import FileVideo from 'lucide-vue-next/dist/esm/icons/file-play.js'; import Folder from 'lucide-vue-next/dist/esm/icons/folder.js'; import Mail from 'lucide-vue-next/dist/esm/icons/mail.js'; import Presentation from 'lucide-vue-next/dist/esm/icons/presentation.js';













import { computed, type Component } from 'vue';
import { hueVars } from '../lib/hue';
import { FILE_KIND_HUE, fileKind, type NxFileKind } from './fileKind';

const props = withDefaults(
  defineProps<{
    name?: string;
    type?: string;
    kind?: NxFileKind;
    size?: 'sm' | 'md' | 'lg';
    /** An outlined tint gives standalone icons more definition on quiet surfaces. */
    look?: 'soft' | 'outlined';
  }>(),
  { name: undefined, type: undefined, kind: undefined, size: 'md', look: 'soft' }
);

const ICONS: Record<NxFileKind, Component> = {
  image: FileImage,
  pdf: FileType2,
  document: FileText,
  spreadsheet: FileSpreadsheet,
  presentation: Presentation,
  archive: FileArchive,
  audio: FileAudio,
  video: FileVideo,
  code: FileCode2,
  text: FileText,
  email: Mail,
  folder: Folder,
  other: File,
};

const resolved = computed(() => props.kind ?? fileKind(props.name, props.type));
const style = computed(() => hueVars(FILE_KIND_HUE[resolved.value], '--tile'));
</script>

<template>
  <span
    class="nx-file-type-icon"
    :class="[`size-${props.size}`, { 'look-outlined': props.look === 'outlined' }]"
    :style="style"
    :data-kind="resolved"
    aria-hidden="true"
  >
    <component :is="ICONS[resolved]" class="nx-file-type-icon__glyph" />
  </span>
</template>

<style scoped>
.nx-file-type-icon {
  --tile-size: var(--nxt-control-size-md);

  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
  width: var(--tile-size);
  height: var(--tile-size);
  border-radius: var(--nxt-radius-md);
  background: var(--tile-subtle-bg);
  color: var(--tile-fg);
}
.nx-file-type-icon.size-sm {
  --tile-size: var(--nxt-control-size-xs);

  border-radius: var(--nxt-radius-sm);
}
.nx-file-type-icon.look-outlined {
  background: color-mix(in oklab, var(--tile) 8%, var(--tile-subtle-bg));
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--tile-border);
}
.nx-file-type-icon.size-lg {
  --tile-size: var(--nxt-control-size-xl);
}
.nx-file-type-icon__glyph {
  width: 55%;
  height: 55%;
}
</style>
